<template>
  <div class="vte-wrap">
    <div
      ref="editor"
      class="vte-body"
      :class="{
        'vte-body--rtl': dir === 'rtl',
        'vte-body--single': singleLine,
        'vte-body--empty': isEmpty
      }"
      :style="bodyStyle"
      :dir="dir"
      :data-placeholder="placeholder"
      contenteditable="true"
      spellcheck="false"
      @input="onInput"
      @keydown="onKeydown"
      @focus="onFocus"
      @paste.prevent="onPaste"
    ></div>
  </div>
</template>

<script>
export default {
  name: 'VarTextEditor',

  props: {
    modelValue: { type: String, default: '' },
    placeholder: { type: String, default: '' },
    rows: { type: Number, default: 3 },
    dir: { type: String, default: 'ltr' },
    singleLine: { type: Boolean, default: false }
  },

  emits: ['update:modelValue', 'focus'],

  data () {
    return {
      isEmpty: true,
      skipWatch: false
    }
  },

  computed: {
    bodyStyle () {
      if (this.singleLine) return {}
      return { minHeight: `calc(${this.rows} * 1.5em + 1rem)` }
    }
  },

  watch: {
    modelValue (newVal) {
      if (this.skipWatch) return
      const cur = this._serialize()
      if ((newVal || '') === cur) return
      this._render(newVal || '')
    }
  },

  mounted () {
    // Event delegation for × buttons — handles both initial render and future chips
    this.$refs.editor.addEventListener('mousedown', e => {
      const btn = e.target.closest('.vte-chip-x')
      if (!btn) return
      e.preventDefault()
      e.stopPropagation()
      const chip = btn.closest('.vte-chip')
      if (chip) {
        chip.remove()
        this._afterDomChange()
      }
    })

    this._render(this.modelValue || '')
  },

  methods: {
    // ── Rendering ──────────────────────────────────────────────────────

    _render (val) {
      const el = this.$refs.editor
      if (!el) return

      // Build new content as a fragment
      const frag = document.createDocumentFragment()
      if (val) {
        const parts = val.split(/({{[^}]+}})/g)
        parts.forEach(part => {
          const m = part.match(/^{{([^}]+)}}$/)
          if (m) {
            frag.appendChild(this._makeChip(m[1]))
          } else if (part) {
            const lines = part.split('\n')
            lines.forEach((line, i) => {
              if (line) frag.appendChild(document.createTextNode(line))
              if (i < lines.length - 1) frag.appendChild(document.createElement('br'))
            })
          }
        })
      }

      el.innerHTML = ''
      el.appendChild(frag)
      this.isEmpty = this._checkEmpty(el)
    },

    _makeChip (varName) {
      const span = document.createElement('span')
      span.className = 'vte-chip'
      span.contentEditable = 'false'
      span.dataset.var = varName
      span.dir = 'ltr'

      const label = document.createElement('span')
      label.className = 'vte-chip-label'
      label.textContent = `{{${varName}}}`

      const btn = document.createElement('button')
      btn.className = 'vte-chip-x'
      btn.tabIndex = -1
      btn.setAttribute('aria-label', 'Remove variable')
      btn.textContent = '×'

      span.appendChild(label)
      span.appendChild(btn)
      return span
    },

    // ── Serialization ──────────────────────────────────────────────────

    _serialize () {
      const el = this.$refs.editor
      if (!el) return ''
      let result = ''

      const walk = nodes => {
        nodes.forEach(node => {
          if (node.nodeType === Node.TEXT_NODE) {
            result += node.textContent
          } else if (node.nodeName === 'BR') {
            result += '\n'
          } else if (node.nodeName === 'SPAN' && node.dataset && node.dataset.var) {
            result += `{{${node.dataset.var}}}`
          } else if (node.nodeName === 'DIV') {
            // Chrome wraps new lines in <div>
            if (result && !result.endsWith('\n')) result += '\n'
            walk([...node.childNodes])
          } else {
            walk([...node.childNodes])
          }
        })
      }

      walk([...el.childNodes])
      return result.replace(/\n$/, '') // strip trailing newline added by browser
    },

    _checkEmpty (el) {
      if (!el) return true
      if (el.querySelector('.vte-chip')) return false
      const text = el.textContent.replace(/[\r\n]/g, '').trim()
      return text === ''
    },

    // ── DOM change handler ─────────────────────────────────────────────

    _afterDomChange () {
      const el = this.$refs.editor
      if (el) this.isEmpty = this._checkEmpty(el)
      const val = this._serialize()
      this.skipWatch = true
      this.$emit('update:modelValue', val)
      this.$nextTick(() => { this.skipWatch = false })
    },

    // ── Event handlers ─────────────────────────────────────────────────

    onInput () {
      this._afterDomChange()
    },

    onKeydown (e) {
      // Prevent newlines in single-line mode
      if (this.singleLine && e.key === 'Enter') {
        e.preventDefault()
        return
      }

      if (e.key !== 'Backspace' && e.key !== 'Delete') return

      const sel = window.getSelection()
      if (!sel || sel.rangeCount === 0) return
      const range = sel.getRangeAt(0)
      if (!range.collapsed) return // Let browser handle non-collapsed selection

      const el = this.$refs.editor
      if (!el) return

      if (e.key === 'Backspace') {
        // Check: cursor is immediately after a chip?
        const { startContainer, startOffset } = range
        let chip = null

        if (startContainer === el && startOffset > 0) {
          const prev = el.childNodes[startOffset - 1]
          if (prev && prev.nodeName === 'SPAN' && prev.dataset && prev.dataset.var) chip = prev
        } else if (startContainer.nodeType === Node.TEXT_NODE && startOffset === 0) {
          const prev = startContainer.previousSibling
          if (prev && prev.nodeName === 'SPAN' && prev.dataset && prev.dataset.var) chip = prev
        }

        if (chip) {
          e.preventDefault()
          chip.remove()
          this._afterDomChange()
        }
      } else {
        // Delete key: check cursor is immediately before a chip?
        const { startContainer, startOffset } = range
        let chip = null

        if (startContainer === el) {
          const next = el.childNodes[startOffset]
          if (next && next.nodeName === 'SPAN' && next.dataset && next.dataset.var) chip = next
        } else if (startContainer.nodeType === Node.TEXT_NODE && startOffset === startContainer.textContent.length) {
          const next = startContainer.nextSibling
          if (next && next.nodeName === 'SPAN' && next.dataset && next.dataset.var) chip = next
        }

        if (chip) {
          e.preventDefault()
          chip.remove()
          this._afterDomChange()
        }
      }
    },

    onFocus () {
      this.$emit('focus', this)
    },

    onPaste (e) {
      const text = e.clipboardData ? e.clipboardData.getData('text/plain') : ''
      if (!text) return

      const sel = window.getSelection()
      if (!sel || !sel.rangeCount) return

      const range = sel.getRangeAt(0)
      range.deleteContents()

      const frag = document.createDocumentFragment()
      const parts = text.split(/({{[^}]+}})/g)
      let lastNode = null

      parts.forEach(part => {
        const m = part.match(/^{{([^}]+)}}$/)
        if (m) {
          lastNode = this._makeChip(m[1])
          frag.appendChild(lastNode)
        } else if (part) {
          const lines = this.singleLine ? [part.replace(/\n/g, '')] : part.split('\n')
          lines.forEach((line, i) => {
            if (line) {
              lastNode = document.createTextNode(line)
              frag.appendChild(lastNode)
            }
            if (i < lines.length - 1) {
              lastNode = document.createElement('br')
              frag.appendChild(lastNode)
            }
          })
        }
      })

      range.insertNode(frag)

      // Move cursor after inserted content
      if (lastNode) {
        const newRange = document.createRange()
        try {
          newRange.setStartAfter(lastNode)
          newRange.collapse(true)
          sel.removeAllRanges()
          sel.addRange(newRange)
        } catch (_) { /* ignore */ }
      }

      this._afterDomChange()
    },

    // ── Public API ─────────────────────────────────────────────────────

    insertVariable (varName) {
      const el = this.$refs.editor
      if (!el) return

      el.focus()

      const chip = this._makeChip(varName)
      const sel = window.getSelection()

      if (sel && sel.rangeCount > 0) {
        const range = sel.getRangeAt(0)
        // Ensure range is inside our editor
        if (el.contains(range.commonAncestorContainer) || range.commonAncestorContainer === el) {
          range.deleteContents()
          range.insertNode(chip)
          range.setStartAfter(chip)
          range.collapse(true)
          sel.removeAllRanges()
          sel.addRange(range)
        } else {
          el.appendChild(chip)
        }
      } else {
        el.appendChild(chip)
      }

      this._afterDomChange()
    }
  }
}
</script>

<style scoped>
.vte-wrap {
  position: relative;
  width: 100%;
}

.vte-body {
  display: block;
  width: 100%;
  padding: 8px 10px;
  font-size: 13px;
  font-family: inherit;
  line-height: 1.5;
  color: var(--text-primary);
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 6px;
  outline: none;
  overflow-y: auto;
  white-space: pre-wrap;
  word-break: break-word;
  box-sizing: border-box;
  transition: border-color 0.15s, box-shadow 0.15s;
  cursor: text;
}

.vte-body:focus {
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
}

.vte-body--single {
  min-height: unset !important;
  max-height: calc(1.5em + 1rem);
  overflow: hidden;
  white-space: nowrap;
}

.vte-body--rtl {
  text-align: right;
}

/* Placeholder via ::before when empty */
.vte-body--empty::before {
  content: attr(data-placeholder);
  color: var(--text-tertiary);
  pointer-events: none;
  cursor: text;
  float: left;
  height: 0;
  width: 100%;
}
</style>

<!-- Non-scoped: dynamically created chip elements don't get Vue's scope attribute -->
<style>
.vte-chip {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 11px;
  font-family: monospace;
  background: #eef2ff;
  color: #3730a3;
  border: 1px solid #6366f1;
  border-radius: 4px;
  padding: 0 2px 0 5px;
  line-height: 1.6;
  vertical-align: middle;
  white-space: nowrap;
  cursor: default;
  user-select: none;
  direction: ltr;
  unicode-bidi: embed;
}

.vte-chip-label {
  pointer-events: none;
}

.vte-chip-x {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  background: none;
  border: none;
  border-radius: 3px;
  color: #6366f1;
  font-size: 13px;
  line-height: 1;
  padding: 0;
  cursor: pointer;
  opacity: 0.6;
  transition: background 0.1s, opacity 0.1s;
  flex-shrink: 0;
}

.vte-chip-x:hover {
  background: #6366f1;
  color: #fff;
  opacity: 1;
}

/* Dark mode chip overrides */
.dark .vte-chip {
  background: rgba(99, 102, 241, 0.15);
  color: #a5b4fc;
  border-color: rgba(99, 102, 241, 0.45);
}

.dark .vte-chip-x {
  color: #a5b4fc;
}

.dark .vte-chip-x:hover {
  background: rgba(99, 102, 241, 0.55);
  color: #fff;
}
</style>
